import { findLast } from "@foretagsplatsen/core-helpers/array";
import { isNotNull } from "../../core/helpers/object-util.js";
import Model from "../Model.js";

/**
 * `BusinessActivity` shows how the result was generated during a
 * period. It is split into two main groups, costs and
 * incomes. Then further by where it originates from (e.g., tax,
 * closing entries).
 *
 * @param {number[]} spec.accumulatedResult - .
 * @param {number[]} spec.costs -
 * @param {number[]} spec.creditedCosts -
 * @param {number[]} spec.costsClosingEntries -
 * @param {number[]} spec.incomeClosingEntries -
 * @param {number[]} spec.costsTaxes -
 * @param {number[]} spec.financialCosts -
 * @param {number[]} spec.financialIncome -
 * @param {number[]} spec.income -
 * @param {number[]} spec.creditedIncome -
 * @param {number[]} spec.incomeTaxes -
 * @param {number[]} spec.periodLabels - E.g., ['Jan', 'Feb', ...]
 * @param {period[]} spec.periods -
 */
export default class BusinessActivity extends Model {
	constructor(spec) {
		super(spec);

		this._accumulatedResult = spec.accumulatedResult.slice();
		this._costs = spec.costs.slice();
		this._creditedCosts = spec.creditedCosts.slice();
		this._costsClosingEntries = spec.costsClosingEntries.slice();
		this._incomeClosingEntries = spec.incomeClosingEntries.slice();
		this._costsTaxes = spec.costsTaxes.slice();
		this._financialCosts = spec.financialCosts.slice();
		this._financialIncome = spec.financialIncome.slice();
		this._income = spec.income.slice();
		this._creditedIncome = spec.creditedIncome.slice();
		this._incomeTaxes = spec.incomeTaxes.slice();
		this._periodLabels = spec.periodLabels.slice();
		this._periods = spec.periods;
	}

	getHighChartsSeries() {
		const highChartsSeries = {};

		highChartsSeries.accumulatedResult = this._createHighChartSerie(
			this._accumulatedResult,
		);

		highChartsSeries.costs = this._createHighChartSerie(this._costs);

		highChartsSeries.creditedCosts = this._createHighChartSerie(
			this._creditedCosts,
		);

		highChartsSeries.incomeClosingEntries = this._createHighChartSerie(
			this._incomeClosingEntries,
		);

		highChartsSeries.costsClosingEntries = this._createHighChartSerie(
			this._costsClosingEntries,
		);

		highChartsSeries.incomeTaxes = this._createHighChartSerie(
			this._incomeTaxes,
		);

		highChartsSeries.costsTaxes = this._createHighChartSerie(
			this._costsTaxes,
		);

		highChartsSeries.income = this._createHighChartSerie(this._income);

		highChartsSeries.creditedIncome = this._createHighChartSerie(
			this._creditedIncome,
		);

		highChartsSeries.financialCosts = this._createHighChartSerie(
			this._financialCosts,
		);

		highChartsSeries.financialIncome = this._createHighChartSerie(
			this._financialIncome,
		);

		highChartsSeries.periodLabels = this._periodLabels;
		highChartsSeries.periods = this._periods;

		return highChartsSeries;
	}

	getLegendValues() {
		const values = {};

		values.income = this._income
			.concat(this._creditedIncome)
			.reduce((entry, total) => entry + total, 0);

		values.costs = this._costs
			.concat(this._creditedCosts)
			.reduce((entry, total) => entry + total, 0);

		values.financialIncome = this._financialIncome
			.concat(this._financialCosts)
			.reduce((entry, total) => entry + total, 0);

		values.incomeClosingEntries = this._incomeClosingEntries
			.concat(this._costsClosingEntries)
			.reduce((entry, total) => entry + total, 0);

		values.costsTaxes = this._costsTaxes
			.concat(this._incomeTaxes)
			.reduce((entry, total) => entry + total, 0);

		// Profit/AccumulatedResult is already summarized on the server
		values.profit = findLast(this._accumulatedResult, isNotNull);

		return values;
	}

	_createHighChartSerie(serie) {
		serie = serie.map((each, i) => {
			const start = this._periods[i].getStart();

			return {
				x: start,
				y: each,
				period: this._periods[i],
			};
		});

		return serie;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.periods = data.periods.map((id) => context.findPeriodById(id));
	}
}
